import * as ScrollArea from "@radix-ui/react-scroll-area";
import * as SelectPrimitive from "@radix-ui/react-select";
import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import SVG from "react-inlinesvg";
import useOnClickOutside from "src/hooks/useOnClickOutside";
import { cn } from "../../../lib/cn";
import { selectVariants } from "./shared";

const SelectContext = createContext({});

export const MultiOptionSelect =
  /**
   * @param {Object} props
   * @param {string} props.placeholder
   * @param {string} [props.collisionBoundarySelector=null]
   * @param {boolean} [props.disabled=false]
   * @param {SelectVariant} [props.variant="tertiary"]
   * @param {string} [props.className]
   * @param {string} [props.maxHeight="300px"]
   * @param {boolean} [props.isOpen=false]
   * @param {(event: React.FocusEvent<HTMLButtonElement>) => void} [props.onOpenChange]
   * @param {(selectedOptions: string[]) => string} [props.summaryFn]
   * @param {React.ReactNode} [props.children]
   * @returns {JSX.Element}
   */
  ({
    collisionBoundarySelector = null,
    className,
    variant = "tertiary",
    disabled = false,
    onOpenChange,
    maxHeight = "300px",
    isOpen,
    children,
    summaryFn = (selectedOptions) => selectedOptions.join(", "),
    placeholder,
  }) => {
    const collisionBoundary = useRef(null);
    const instanceIdRef = useRef(Math.random().toString(36).slice(2));
    const [items, setItems] = useState({}); // { [key]: boolean }
    const [open, setOpen] = useState(isOpen);
    const [dynamicMaxHeight, setDynamicMaxHeight] = useState(maxHeight);
    const triggerRef = useRef(null);
    const contentRef = useRef(null);

    useOnClickOutside([triggerRef, contentRef], () => {
      setOpen(false);
    });

    const handleOpenChange = (open) => {
      setOpen(open);

      if (open) {
        // Announce that this select has opened so others can close
        try {
          window?.dispatchEvent(new CustomEvent("sudokit:select-open", { detail: { id: instanceIdRef.current } }));
        } catch (_) {}
      }

      if (onOpenChange) {
        onOpenChange(open);
      }
    };

    // called by items on mount/update/unmount
    const register = (key, isOn) => {
      setItems((prev) => ({ ...prev, [key]: isOn }));
    };
    const unregister = (key) => {
      setItems((prev) => {
        const nxt = { ...prev };
        delete nxt[key];
        return nxt;
      });
    };

    const label = summaryFn
      ? summaryFn(
          Object.entries(items)
            .filter(([_, on]) => on)
            .map(([k]) => k)
        )
      : (() => {
          const sel = Object.entries(items).filter(([_, on]) => on);
          return sel.length ? sel.map(([k]) => k).join(", ") : placeholder;
        })();

    useEffect(() => {
      if (!collisionBoundarySelector) {
        return;
      }

      collisionBoundary.current = document.querySelector(collisionBoundarySelector);
    }, [collisionBoundarySelector]);

    // Update dynamic maxHeight when menu opens
    // so that if the full height of the menu is not visible, the menu will scroll
    useEffect(() => {
      if (open && triggerRef.current) {
        const triggerRect = triggerRef.current.getBoundingClientRect();
        const spaceBelow = window.innerHeight - triggerRect.bottom - 16;
        const spaceAbove = triggerRect.top - 16;
        const availableSpace = Math.max(spaceBelow, spaceAbove, 120);
        setDynamicMaxHeight(`${availableSpace}px`);
      }
    }, [open]);

    useEffect(() => {
      setOpen(!!isOpen);
    }, [isOpen]);

    // Close when another sudokit select opens
    useEffect(() => {
      const handler = (e) => {
        if (e && e.detail && e.detail.id !== instanceIdRef.current) {
          setOpen(false);
        }
      };
      window?.addEventListener("sudokit:select-open", handler);
      return () => window?.removeEventListener("sudokit:select-open", handler);
    }, []);

    return (
      <SelectContext.Provider value={{ register, unregister }}>
        <>
          <SelectPrimitive.Root
            value={label}
            disabled={disabled}
            open={open}
            onOpenChange={handleOpenChange}
          >
            <SelectPrimitive.Trigger
              className={`sudokit ${cn(
                selectVariants({ variant })
              )} group flex items-center h-[40px] justify-between border-solid [&&]:rounded-[10px] bg-app-document-background px-3 py-2 shadow-[0_1px_2px_0_rgba(0,0,0,0.08)]`}
              aria-label={label}
              ref={triggerRef}
            >
              <div className="flex items-center">
                <SelectPrimitive.Value>{label}</SelectPrimitive.Value>
              </div>
              <SelectPrimitive.Icon>
                <SVG
                  src="/assets/settings_dropdown_icon.svg"
                  className={`ml-4 mt-1 h-4 w-4 ${cn(selectVariants({ variant: `icon${variant}` }))}`}
                />
              </SelectPrimitive.Icon>
            </SelectPrimitive.Trigger>

            <SelectPrimitive.Portal>
              <SelectPrimitive.Content
                role="dialog"
                avoidCollisions={true}
                collisionBoundary={collisionBoundary.current}
                position="popper"
                side="bottom"
                collisionPadding={{ top: 100, bottom: 100 }}
                className={`text-start origin-top [&[data-state='open']]:animate-flyout w-[457px] bg-app-menu-color shadow-card border border-app-menu-color-hover my-2 rounded-xl overflow-hidden z-[10000] ${className}`}
                onCloseAutoFocus={(e) => e.preventDefault()}
                style={{ maxHeight: dynamicMaxHeight }}
                ref={contentRef}
              >
                <ScrollArea.Root
                  className="w-full"
                  type="auto"
                  style={{
                    maxHeight: dynamicMaxHeight,
                    overflowY: "auto",
                  }}
                >
                  {children}
                  <ScrollArea.Scrollbar
                    className="w-2 bg-gray-200 transition-colors duration-200 rounded-xl h-[97%]"
                    orientation="vertical"
                  >
                    <ScrollArea.Thumb className="bg-gray-300 rounded-xl" />
                  </ScrollArea.Scrollbar>
                </ScrollArea.Root>
              </SelectPrimitive.Content>
            </SelectPrimitive.Portal>
          </SelectPrimitive.Root>
        </>
      </SelectContext.Provider>
    );
  };

// A helper hook for any child component:
export function useSelectItem(key, isOn) {
  const { register, unregister } = useContext(SelectContext);
  useEffect(() => {
    register(key, isOn);
    return () => unregister(key);
  }, [key, isOn]);
}
